import { apiClient } from "../../../config/axios.config";
import type { Response } from "../../../types/api.types";
import type { getPartnerListForm, partnerForm, partnerImageUploadForm, partnerQuotationReferenceForm } from "../dtos/request/partner.request";
import type { PartnerDetailResponse, PartnerPageResult, PartnerStoreItem, PartnerTypeItem } from "../dtos/response/partner.response";
import type { newQuotationForm } from "../dtos/request/quotation.request";

const model = "/v1/partner";

// ───────── 8-1. 제휴사 관리 ─────────

export const getPartnerList = async (query: getPartnerListForm): Promise<Response<PartnerPageResult>> => {
  const { data } = await apiClient.get(model, { params: query });
  return data;
};

/** 공급업체로 연결 가능한 지점 목록 — GET /v1/partner/store (파라미터 없음, 배열 응답) */
export const getPartnerStoreList = async (): Promise<Response<PartnerStoreItem[]>> => {
  const { data } = await apiClient.get(`${model}/store`);
  return data;
};

/** 제휴사 유형 목록 — GET /v1/partner/type (partnerType 코드 + partnerTypeName 표시명) */
export const getPartnerTypeList = async (): Promise<Response<PartnerTypeItem[]>> => {
  const { data } = await apiClient.get(`${model}/type`);
  return data;
};

export const getPartnerDetail = async (partnerId: string): Promise<Response<PartnerDetailResponse>> => {
  const { data } = await apiClient.get(`${model}/${partnerId}`);
  return data;
};

export const createPartner = async (form: partnerForm): Promise<Response<PartnerDetailResponse>> => {
  const { data } = await apiClient.post(model, form);
  return data;
};

export const updatePartner = async ({ partnerId, form }: { partnerId: string; form: partnerForm }): Promise<Response<PartnerDetailResponse>> => {
  const { data } = await apiClient.patch(`${model}/${partnerId}`, form);
  return data;
};

/**
 * 제휴사 이미지 업로드/삭제 — POST /v1/partner/{partnerId}/upload
 * query: imageType(BANNER|PROFILE), delete / body: multipart(multipartFile)
 */
export const uploadPartnerImage = async ({ partnerId, imageType, file, delete: remove = false }: partnerImageUploadForm): Promise<Response<PartnerDetailResponse>> => {
  const formData = new FormData();
  if (file) formData.append("multipartFile", file);

  const { data } = await apiClient.post(`${model}/${partnerId}/upload`, formData, {
    params: { imageType, delete: remove },
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

/** TODO(backend): 삭제 API 요청 중 — 경로 확정되면 맞출 것 (가정: DELETE /v1/partner/{partnerId}) */
export const deletePartner = async (partnerId: string): Promise<Response> => {
  const { data } = await apiClient.delete(`${model}/${partnerId}`);
  return data;
};

// ───────── 8-2. 제휴사 시세표 관리 ─────────
// 시세표 데이터 구조는 지점 시세표(newQuotationForm)와 동일

export const getPartnerQuotation = async (partnerId: string): Promise<Response<newQuotationForm>> => {
  const { data } = await apiClient.get(`${model}/quotation/${partnerId}`);
  return data;
};

export const postPartnerQuotation = async ({ partnerId, form }: { partnerId: string; form: newQuotationForm }): Promise<Response<newQuotationForm>> => {
  const { data } = await apiClient.post(`${model}/quotation/${partnerId}`, form);
  return data;
};

/** 다른 업체(지점) 시세표를 만원 단위 가감해서 불러오기 — 제출이 아닌 조회 */
export const getPartnerQuotationReference = async ({ storeId, ...params }: partnerQuotationReferenceForm): Promise<Response<newQuotationForm>> => {
  const { data } = await apiClient.get(`${model}/quotation/reference/${storeId}`, { params });
  return data;
};
